import { Link, useForm, router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import I from "../../components/Icons";
import { getLanguage, pick } from "../../lib/i18n";

const copy = {
  pl: { title: "Produkty", subtitle: "Wszystkie produkty. Wybierz produkt lub utwórz nowy.", newProduct: "Nowy produkt", noProducts: "Brak produktów", noProductsHint: "Utwórz pierwszy produkt, żeby zacząć generować grafiki.", untitled: "Bez nazwy", deleteProduct: "Usuń produkt", confirmDelete: (name: string) => `Usunąć produkt „${name}" wraz ze wszystkimi plikami?`, noDescription: "Brak opisu - wgraj plik produktu." },
  en: { title: "Products", subtitle: "All products. Choose a product or create a new one.", newProduct: "New product", noProducts: "No products", noProductsHint: "Create your first product to start generating visuals.", untitled: "Untitled", deleteProduct: "Delete product", confirmDelete: (name: string) => `Delete product "${name}" with all its files?`, noDescription: "No description – upload a product file." },
};

interface Product {
  id: number;
  name: string;
  description_md?: string;
  parsed_at?: string;
  updated_at: string;
}

interface Props {
  products: Product[];
  current_user: { name: string; email: string; role: string };
}

export default function ProductsIndex({ products, current_user }: Props) {
  const t = pick(copy);
  const { post, processing } = useForm();

  const canEdit = current_user.role === "admin" || current_user.role === "moderator";
  const createProduct = () => post("/products");

  const initials = current_user.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <AppShell
      products={products}
      user={{ name: current_user.name, initials, role: current_user.role }}
    >
      <div className="page">
        <div className="page-hd">
          <div>
            <h2>{t.title}</h2>
            <p>{t.subtitle}</p>
          </div>
          {canEdit && (
            <div className="right">
              <button className="btn btn-primary btn-sm" onClick={createProduct} disabled={processing}>
                {I.plus} {t.newProduct}
              </button>
            </div>
          )}
        </div>

        {products.length === 0 ? (
          <div className="empty">
            <h4>{t.noProducts}</h4>
            <p>{t.noProductsHint}</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {products.map((p) => (
              <div key={p.id} className="panel">
                <div className="panel-hd" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Link href={`/products/${p.id}`} style={{ textDecoration: "none", flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                    <h3 style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0 }}>{p.name || t.untitled}</h3>
                    {p.parsed_at && (
                      <span className="mono" style={{ fontSize: 9.5, color: "var(--accent-ink)", background: "var(--accent-soft)", padding: "1px 5px", borderRadius: 2, flexShrink: 0 }}>
                        parsed
                      </span>
                    )}
                  </Link>
                  {canEdit && (
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      title={t.deleteProduct}
                      style={{ flexShrink: 0 }}
                      onClick={() => {
                        if (confirm(t.confirmDelete(p.name || t.untitled))) {
                          router.delete(`/products/${p.id}`);
                        }
                      }}
                    >
                      {I.x}
                    </button>
                  )}
                </div>
                <Link href={`/products/${p.id}`} style={{ textDecoration: "none", display: "block" }}>
                  <div className="panel-body">
                    <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
                      {p.description_md
                        ? p.description_md.slice(0, 120) + "…"
                        : t.noDescription}
                    </div>
                    <div className="mono" style={{ fontSize: 10, color: "var(--muted-2)", marginTop: 8, letterSpacing: 0.04 }}>
                      {new Date(p.updated_at).toLocaleDateString(getLanguage() === "en" ? "en-GB" : "pl-PL")}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
